(ns ycweb.components.appcommon
  (:require
   #?@(:cljs
       [[cljs.reader :as edn]]
       :clj
       [[clojure.edn :as edn]])
   [ajax.core :as ajax]
   [rum.core :as rum]

   [ycweb.util.comms :as comms]
   [ycweb.util.core :as u]
   [ycweb.util.urlstate :as urlstate]
   [ycweb.util.hash :as hash]

   [ycweb.components.state :as state]
   [ycweb.components.flow :as flow]
   [ycweb.components.common :as cmn]
   [ycweb.components.toaster :as toaster]
   [ycweb.components.grid :as grid]
   [ycweb.components.transitions :as t]
   [ycweb.components.versioncheck :as versioncheck]))

(def recaptcha-failure-msg
  "Shown when no reCAPTCHA token arrives (see on-timeout below). Matches the
   server's rejection message in orchestration.servicedef/wrap-captcha."
  "We couldn't complete a quick security check. Please refresh the page and try again.")

#?(:cljs
   (defn- start-token-timeout
     "Calls on-timeout unless the returned atom is set (a token arrived) within
      15s. Started before js/grecaptcha is touched, so a script that never
      loaded still times out. reCAPTCHA is never skipped: without a token
      nothing is sent, on-timeout only lets the caller clear its loading state."
     [on-timeout]
     (let [token? (atom false)]
       (when on-timeout
         (js/setTimeout #(when-not @token? (on-timeout)) 15000))
       token?)))

(defn recaptcha
  "Optional on-timeout (no args): see start-token-timeout."
  [app-atom captcha-site-key dispatch-key config & [on-timeout]]
  #?(:cljs
     (let [token? (start-token-timeout on-timeout)]
       (.ready js/grecaptcha
         #(->
            (.execute js/grecaptcha
              captcha-site-key #js {:action "submit"})
            (.then
              (fn [captcha-token]
                (reset! token? true)
                (grid/dispatch-action app-atom dispatch-key
                  (merge config
                    {:captcha-token captcha-token})))))))))

(defn recaptcha-then
  "Run invisible reCAPTCHA v3 and call (f captcha-token) with the resulting token.
   Continuation variant of `recaptcha` for callers that proceed via a closure
   (e.g. a model fn that sends a request) rather than a dispatched grid action.
   Optional on-timeout (no args): see start-token-timeout."
  [captcha-site-key f & [on-timeout]]
  #?(:cljs
     (let [token? (start-token-timeout on-timeout)]
       (.ready js/grecaptcha
         #(->
            (.execute js/grecaptcha
              captcha-site-key #js {:action "submit"})
            (.then
              (fn [captcha-token]
                (reset! token? true)
                (f captcha-token))))))))

(defmethod comms/receive :service/error [app-atom e]
  (let [error-message (or (:message e) (:msg e))]
    (toaster/close-all-overlays app-atom)
    (cond
      (= (:response/type e) :critical)
      (do
        (toaster/close-subtle-progress app-atom)
        (grid/message-modal app-atom
          {:title "Error"
           :text error-message
           :type :error
           :confirm-text "OK"}))

      (= (:response/type e) :unauthenticated)
      (do
        (swap! app-atom assoc-in [:redirect :login] true)
        (urlstate/raw-redirect app-atom "/login.htm"))

      (or (= (:response/type e) :permission) (= (:response/type e) :redirect))
      (cond
        (u/presence (:root (:response/redirect e)))
        (urlstate/redirect app-atom (:root (:response/redirect e)) (or (:action (:response/redirect e)) ""))

        (:response/redirect e)
        (do
          (swap! app-atom assoc-in [:redirect :uri] (:url (:response/redirect e)))
          (urlstate/redirect! (:url (:response/redirect e)))))

      :else
      (toaster/open-toast app-atom :error error-message {:show-close-button true}))
    (println "Error: " error-message)
    (swap! app-atom assoc :error error-message :error-field nil)))

(defn hydrated?
  "True when this mount was server-rendered."
  [app-atom]
  (boolean (get-in @app-atom [:app :hydrated?])))

(defn bind-urlstate [app-atom {:keys [fragment? base-prefix screen-action?] :as opts}]
  ;; We need a proxy atom so that writes to the proxy can trigger a t/transition-next
  ;; instead of just circumventing all the mechanisms between.
  (let [screen-atom (rum/cursor-in app-atom [:page])
        proxy-atom (atom @screen-atom)

        ;; The server resolves the screen with a db lookup we can't repeat — all
        ;; we see is that an id was supplied, not whether it matched. On a
        ;; hydrated mount its answer wins for the first resolution, or we
        ;; navigate off the page it just rendered and refetch.
        ssr-screen (when (hydrated? app-atom) (:screen @screen-atom))
        settled? (atom false)]
    (add-watch screen-atom ::urlstate
      (fn [k r o n]
        (when (not (= (:screen o) (:screen n)))
          (reset! proxy-atom n))))

    (add-watch proxy-atom ::urlstate
      (fn [k r o n]
        (let [screen (:screen n)
              server-knows-better?
              (and
                (not @settled?) ssr-screen
                (contains? (methods flow/render-screen) ssr-screen)
                (not= screen ssr-screen))]
          (reset! settled? true)
          (if server-knows-better?
            (reset! proxy-atom (assoc n :screen ssr-screen))
            (when
              (or
                (and
                  (not= (:screen o) screen)
                  (not= (:screen @screen-atom) screen))
                (not= (get o screen) (get n screen)))
              (t/transition-next app-atom screen))))))

    (urlstate/setup-history app-atom opts)
    (urlstate/bind-root-fragment (rum/cursor proxy-atom :screen))))

(defn setup-mobile [app-atom]
  (swap! app-atom assoc-in [:login :store_id]
    (u/parseInt (u/get-cookie! app-atom :store_id)))

  ;; TODO: Really want to do this on a component level, instead of on a global level like this..
  ;; #pickingbattles.
  (cmn/lifecycle-event :setup app-atom))

(rum/defc root [app-atom]
  (flow/flow app-atom false))

(defn record-scroll [app-atom e]
  (let [scroll (state/scroll-position)]
    #?(:cljs
       (.setTimeout js/window
         (fn []
           (let [newscroll (state/scroll-position)]
             (when (= newscroll scroll)
               (swap! app-atom assoc-in [:device :scroll] newscroll))))
         200))))

(defn record-screensize [app-atom e]
  (let [size (state/screen-size)]
    #?(:cljs
       (.setTimeout js/window
         (fn []
           (let [newsize (state/screen-size)]
             (when (= newsize size)
               (swap! app-atom assoc-in [:device :screen] newsize))))
         200))))

(defn init [app-atom]
  ;; Always listen for these - used on blitz.
  #?(:cljs
     (set! (.-scanSuccess js/window)
       (fn [scan]
         (flow/barcode-dispatch app-atom scan))))

  #?(:cljs
     (set! js/dispatchSearchAction
       (fn [action data]
         (grid/dispatch-action app-atom (keyword action) (js->clj data :keywordize-keys true)))))

  #?(:cljs
     (set! js/artificialBack
       (fn [] (flow/back-button app-atom)))))

(defn hydrate [app-state new-state]
  (merge
    app-state (dissoc new-state :device)))

(defn start-complete!
  "Finishes startup for a server-rendered mount, which never fetches :web/start.
   Call after rum/hydrate so the mount transition runs against a mounted DOM."
  [app-atom]
  (swap! app-atom assoc :is-started true)
  (t/transition-mount app-atom))

(defn entry-main [element-id app-atom refresh]
  #?(:cljs
     (if (get-in @app-atom [:app :started])
       (do
         (refresh)
         (println "Already started, don't register stuff again.")
         (.getElementById js/document element-id))
       (do
         (println "Setting up.")
         (.addEventListener js/document "click"
           #(swap! app-atom assoc-in [:device :pos] {:x (.-x %) :y (.-y %)}) true)
         (.addEventListener js/window "scroll" (partial record-scroll app-atom))
         (.addEventListener js/window "resize" (partial record-screensize app-atom))
         (.addEventListener js/window "onresize" (partial record-screensize app-atom))

         (set-validator! app-atom
           (fn [state]
             (if
               (nil? state)
               (do
                 (js/console.warn (str "### !!! app-atom is being set to nil !!! ###"))
                 false)

               true)))

         (when-let [pos (.getElementById js/document element-id)]
          (let [url (.getAttribute pos "data-endpoint")
                state (.getAttribute pos "data-state")
                csrf-token (.getAttribute pos "csrf-token")]

           (swap! app-atom assoc-in [:app :started] true)

           (when csrf-token (swap! app-atom assoc :csrf-token csrf-token))

           ;; This is likely going to be far too large to be feasible.
           #_(history/setup-listener app-atom)
           (u/now)
           (when state
             #_(println (edn/read-string "#inst \"2021-02-26T08:47:24.751Z\""))
             (try
               (swap! app-atom hydrate (edn/read-string (hash/base64-decode state)))
               (swap! app-atom assoc-in [:app :hydrated?] true)
               (catch #?(:cljs js/Error :clj Exception) e
                 #?(:cljs (.log js/console e)
                    :clj  (.printStackTrace e)))))

           (when (and url (contains? #{"website-react-mount" "minicart-react-mount" "keepaside-react-mount" "header-react-mount" "admin-react-mount"} element-id))
             ;; A server-rendered mount already has the :web/start payload in
             ;; its data-state — the caller finishes startup via start-complete!
             (comms/setup-ajax app-atom {:fetch-start? (not (hydrated? app-atom))}))

           (when (and url (not (contains? #{"website-react-mount" "keepaside-react-mount" "header-react-mount"} element-id)))
             (comms/setup-websocket url app-atom
               (fn [status]
                 (cond
                   (and status (= (cmn/curact app-atom [:app :version]) "dev"))
                   (toaster/open-toast app-atom :success "Connection opened" {})
                   (not status)
                   (toaster/open-toast app-atom :error "Connection lost" {}))))
             (versioncheck/start-version-check app-atom))

           (init app-atom)

           (flow/setup-keys app-atom refresh)
           (t/setup-transition-listener app-atom)
           (setup-mobile app-atom)

           pos))))))